'use client';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { WEBAPP_URL } from '@/constants/http-constants';

import { addUtmParams } from '@/helpers/utm-helper';
import { cn } from '@cineamo/ui/src/helpers/cn';

import { DROPDOWN_DIVIDER_CLASSNAME, DROPDOWN_MENU_ITEM_CLASSNAME } from '@cineamo/ui/src/dropdown/dropdown-colors';
import DropdownPanel from '@cineamo/ui/src/dropdown/DropdownPanel';
import { IconMenu24 } from '@cineamo/ui/src/icons/ic-menu';
import { CookiePreferencesModal } from '@cineamo/ui/src/modals/CookiePreferencesModal';

import { Link } from '@/i18n/routing';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

const BurgerMenu = () => {
    const domainConfiguration = useDomainConfiguration();
    const t = useTranslations();
    const [cookiePreferencesModalIsOpen, setCookiePreferencesModalIsOpen] = useState(false);

    const cinema = domainConfiguration.cinema;
    const websiteConfiguration = cinema?._embedded?.cinemaConfiguration?.websiteConfiguration;
    const cinemaHasCustomImprint = (websiteConfiguration?.imprints?.length ?? 0) > 0 || !!websiteConfiguration?.imprint;
    const cinemaHasTermsAndConditions =
        !!cinema?._embedded?.cinemaConfiguration?.websiteConfiguration?.termsAndConditionsUrl;
    const linkClass = cn('py-8 px-16 text-left text-body-14 whitespace-nowrap', DROPDOWN_MENU_ITEM_CLASSNAME);
    return (
        <div className="absolute right-9 lg:right-16 top-1/2 transform -translate-y-1/2">
            {cookiePreferencesModalIsOpen && (
                <CookiePreferencesModal
                    modalIsOpen={cookiePreferencesModalIsOpen}
                    setModalIsOpen={setCookiePreferencesModalIsOpen}
                    // A cinema website has no `/data-protection` route of its own; the web app hosts it.
                    privacyPolicyUrl={addUtmParams(`${WEBAPP_URL}/data-protection`, {
                        utmSource: 'cinema_website',
                        utmContent: 'booking-burger_menu-cookie_preferences_modal-data_protection',
                        utmCampaign: `cinema_${domainConfiguration.cinema.id}-${domainConfiguration.cinema.name}`,
                        utmMedium: 'website'
                    })}
                />
            )}
            <Popover className="relative">
                <PopoverButton>
                    <IconMenu24 />
                </PopoverButton>
                <DropdownPanel
                    as={PopoverPanel}
                    anchor={{ to: 'bottom end', gap: '8px' }}
                    className="rounded-8 z-[110]">
                    {/* `divide-white/50` before: invisible on a light panel and wrong on a dark
                        themed one. The tint is the panel's own hairline colour. */}
                    <ul className={cn('flex flex-col divide-y', DROPDOWN_DIVIDER_CLASSNAME)}>
                        <Link
                            className={linkClass}
                            href={addUtmParams(`${WEBAPP_URL}/data-protection`, {
                                utmSource: 'cinema_website',
                                utmContent: 'booking-burger_menu-data_protection',
                                utmCampaign: `cinema_${domainConfiguration.cinema.id}-${domainConfiguration.cinema.name}`,
                                utmMedium: 'website'
                            })}>
                            {t('footer.data-protection')}
                        </Link>
                        <Link
                            className={linkClass}
                            href={
                                cinemaHasCustomImprint
                                    ? '/imprint'
                                    : addUtmParams(`${WEBAPP_URL}/imprint`, {
                                          utmSource: 'cinema_website',
                                          utmContent: 'booking-burger_menu-imprint',
                                          utmCampaign: `cinema_${domainConfiguration.cinema.id}-${domainConfiguration.cinema.name}`,
                                          utmMedium: 'website'
                                      })
                            }>
                            {t('footer.imprint')}
                        </Link>
                        <Link
                            className={linkClass}
                            href={'/accessibility-statement'}>
                            {t('footer.accessibility-statement')}
                        </Link>
                        {cinemaHasTermsAndConditions && (
                            <Link
                                className={linkClass}
                                href={
                                    cinema?._embedded?.cinemaConfiguration?.websiteConfiguration
                                        ?.termsAndConditionsUrl ?? ''
                                }>
                                {t('footer.terms-and-conditions')}
                            </Link>
                        )}
                        <button
                            className={linkClass}
                            onClick={() => setCookiePreferencesModalIsOpen(true)}>
                            {t('footer.data-protection-settings')}
                        </button>
                    </ul>
                </DropdownPanel>
            </Popover>
        </div>
    );
};

export default BurgerMenu;
